import { z } from 'zod';
import { jArticleListSchema, jArticleLittleSchema } from './article';
import { audienceAutocompleteSchema } from './autocomplete';
import { rightsSchema } from './common';
import { jObjectLittleSchema } from './objectBase';
import { jUserLittleSchema } from './user';
import { jHrefSchema } from './utils';
import { articleDisplayModeSchema, widgetVectorSchema } from './widgets/common';
import { combinedWidgetContentSchema, widgetStatePropsSchema, widgetWrapperPropsSchema } from './widgets/default';

export const jArticleWidgetLittle = jArticleLittleSchema.extend({
    articleWidgets: widgetWrapperPropsSchema.array(),
    articleWidgetDisplayMode: articleDisplayModeSchema.nullable().or(z.undefined()),
});

export type jArticleWidgetLittle = z.infer<typeof jArticleWidgetLittle>;

export const widgetLayerSchema = z.object({
    uniqid: z.string(),
    title: z.string(),
    locked: z.boolean(),
    visible: z.boolean(),
    widgets: widgetWrapperPropsSchema.array(),
    position: widgetVectorSchema.optional(),
});
export type WidgetLayer = z.infer<typeof widgetLayerSchema>;

export const widgetDisplayModeSchema = z.enum(['edit', 'view', 'preview']);
export type WidgetDisplayMode = z.infer<typeof widgetDisplayModeSchema>;

export const widgetDefaultProps = z.object({
    width: z.number().or(z.undefined()).optional(),
    inplace: z.boolean(),
    isComponentVisible: z.boolean().optional(),
    mode: widgetDisplayModeSchema,
    onClick: z.function().returns(z.void()).optional(),
    onChange: z.function().args(combinedWidgetContentSchema, z.boolean()).returns(z.void()),
    onStateChange: z.function().args(widgetStatePropsSchema).returns(z.void()),
});
export type WidgetDefaultProps = z.infer<typeof widgetDefaultProps>;

export const widgetCheckListContentArrForFrontSchema = z.object({
    text: z.string(),
    value: z.boolean().optional(),
    links: jHrefSchema.array().optional(),
    uri: z.string().optional(),
    mimetype: z.string().optional(),
    id: z.number().optional(),
    user: jUserLittleSchema.optional(),
});
export type WidgetCheckListContentArrForFront = z.infer<typeof widgetCheckListContentArrForFrontSchema>;

export const widgetCheckListContentForFrontSchema = z.object({
    arr: widgetCheckListContentArrForFrontSchema.array().optional(),
    edit: z.boolean().optional(),
});
export type WidgetCheckListContentForFront = z.infer<typeof widgetCheckListContentForFrontSchema>;

export const widgetArticleCheckListTypeForFrontSchema = z.object({
    name: z.literal('widget-check-list'),
    uniqid: z.string(),
    content: widgetCheckListContentForFrontSchema,
    title: z.string(),
});
export type WidgetCheckListTypeForFront = z.infer<typeof widgetArticleCheckListTypeForFrontSchema>;

export const widgetModalPropsSchema = z.object({
    title: z.string().optional(),
    view: z.any().optional(),
});
export type WidgetModalProps = z.infer<typeof widgetModalPropsSchema>;

export const widgetRtObjectSchema = z.object({
    uniqid: z.string(),
    fn: z.enum(['widget-presence-response', 'check-list-response', 'widget-update', 'widget-quick-survey-response']),
    object: jObjectLittleSchema,
});
export type WidgetRtObject = z.infer<typeof widgetRtObjectSchema>;

export const widgetStylesSchema = z.record(z.string(), z.record(z.string(), z.string().or(z.number())));
export type WidgetStyles = z.infer<typeof widgetStylesSchema>;

export const widgetContextSchema = z.enum(['comment', 'article']);
export type WidgetContext = z.infer<typeof widgetContextSchema>;

export const widgetsStateSchema = z.object({
    token: z.string().or(z.undefined()).optional(),
    ids: z.record(z.string(), widgetWrapperPropsSchema),
    layers: z.record(z.string(), widgetLayerSchema),
    states: z.record(z.string(), widgetStatePropsSchema),
    modal: widgetModalPropsSchema.or(z.undefined()),
    editableMap: z.record(
        z.string(),
        z.object({
            index: z.number(),
            uniqid: z.string(),
        }),
    ),
    currentEditableIndex: z.number(),
    currentEditableWidgetId: z.string().or(z.undefined()),
    widgetWrapperStyle: widgetStylesSchema,
    widgetTitleStyle: widgetStylesSchema,
    widgetObject: z.record(z.string(), jObjectLittleSchema.partial()),
    widgetObjectRights: z.record(z.string(), rightsSchema),
    widgetChipCount: z.record(z.string(), z.number()),
    widgetAuthor: z.record(z.string(), jUserLittleSchema.partial()),
    flushedWidgets: z.string().array(),
    rtObjectStack: widgetRtObjectSchema.array(),
    widgetContext: z.record(z.string(), widgetContextSchema),
});
export type WidgetsState = z.infer<typeof widgetsStateSchema>;

export const widgetsRootStateSchema = z.object({
    widgets: widgetsStateSchema,
});
export type WidgetsRootState = z.infer<typeof widgetsRootStateSchema>;

export const jArticleWidgetViewSchema = jArticleListSchema.extend({
    articleWidgets: widgetWrapperPropsSchema.array(),
    articleWidgetDisplayMode: articleDisplayModeSchema.nullable().or(z.undefined()),
    articleWidgetsCover: z.number().or(z.undefined()),
    _audience: audienceAutocompleteSchema.array(),
});

export type jArticleWidgetView = z.infer<typeof jArticleWidgetViewSchema>;

export * from './widgets/common';
export * from './widgets/default';
export * from './widgets/ensure';
export * from './widgets/legacy';
export * from './widgets/widgetArticleAttachment';
export * from './widgets/widgetArticleButton';
export * from './widgets/widgetArticleGallery';
export * from './widgets/widgetArticleImage';
export * from './widgets/widgetArticleSlider';
export * from './widgets/widgetArticleText';
export * from './widgets/widgetArticleTitle';
export * from './widgets/widgetCalendarCalDav';
export * from './widgets/widgetCheckList';
export * from './widgets/widgetContactCardDav';
export * from './widgets/widgetDatasourceTable';
export * from './widgets/widgetEmailsImap';
export * from './widgets/widgetExcelDatasourceTable';
export * from './widgets/widgetLuccaAbsence';
export * from './widgets/widgetPresence';
export * from './widgets/widgetQuickSurvey';
